/**
 * SpatialColumnsSection — renders the coordinate-columns list inside the
 * MetaInfo manager (per-database dialog) and the global Settings → Meta Info
 * view. Each declaration is a row showing the qualified column name, the
 * format (lat-lng pair / WKT / GeoJSON / lat-lng text), and a Remove button.
 * Mirrors EnumColumnsSection; declared coordinate columns are a MetaInfo kind
 * (T18) with no value cache.
 */

import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SpatialColumnDeclaration } from '@/types/electron';

const FORMAT_BADGE: Record<SpatialColumnDeclaration['format'], string> = {
  'lat-lng-pair': 'lat/lng pair',
  wkt: 'WKT',
  geojson: 'GeoJSON',
  'latlng-text': 'lat/lng text',
};

export type SpatialColumnsSectionProps = {
  declarations: SpatialColumnDeclaration[];
  onRemove: (decl: SpatialColumnDeclaration) => void;
};

export function SpatialColumnsSection({ declarations, onRemove }: SpatialColumnsSectionProps) {
  if (declarations.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        No coordinate columns declared yet.
      </div>
    );
  }

  return (
    <>
      {declarations.map((d) => (
        <div
          key={d.id}
          className="flex flex-col gap-1 border rounded p-2"
          data-testid={`metainfo-spatial-row-${d.id}`}
        >
          <div className="flex items-center justify-between gap-2 text-xs">
            <span translate="no" className="font-mono truncate min-w-0">
              {d.schema}.{d.table}.{d.column}
              {d.format === 'lat-lng-pair' && d.lngColumn ? ` + ${d.lngColumn}` : ''}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <span
                className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
                data-testid={`metainfo-spatial-format-${d.id}`}
              >
                {FORMAT_BADGE[d.format]}
                {d.format === 'latlng-text' && d.axisOrder === 'lng-lat' ? ' (lng,lat)' : ''}
              </span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onRemove(d)}
                aria-label="Remove coordinate-column declaration"
                title="Remove coordinate-column declaration"
                className="h-6 w-6 text-destructive"
                data-testid={`metainfo-spatial-remove-${d.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}
